/**
 * Product Results section: vertical tab switching for product / stats / media panels.
 */

(function () {
  'use strict';

  const VERTICAL_ARROW_KEYS = {
    ArrowDown: 1,
    ArrowUp: -1
  };

  function pausePanelVideos(panel) {
    if (!panel) return;

    for (const video of panel.querySelectorAll('video')) {
      if (video instanceof HTMLVideoElement) {
        video.pause();
      }
    }
  }

  function playPanelVideos(panel) {
    if (!panel) return;

    for (const video of panel.querySelectorAll('video')) {
      if (!(video instanceof HTMLVideoElement)) continue;

      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(function () {
          /* autoplay may be blocked */
        });
      }
    }
  }

  function snapStatListToNearest(statList) {
    const stats = statList.querySelectorAll('.product-results__stat');
    if (!stats.length) return;

    const scrollTop = statList.scrollTop;
    let nearestStat = stats[0];
    let nearestDistance = Infinity;

    for (const stat of stats) {
      const distance = Math.abs(stat.offsetTop - scrollTop);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestStat = stat;
      }
    }

    const targetTop = nearestStat.offsetTop;
    if (Math.abs(scrollTop - targetTop) <= 1) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    statList.scrollTo({
      top: targetTop,
      behavior: prefersReducedMotion ? 'auto' : 'smooth'
    });
  }

  function initStatListScroll(statList, shouldSnap) {
    if (!statList || statList.dataset.statScrollInited === 'true') return;
    statList.dataset.statScrollInited = 'true';

    let scrollTimer;

    function onScrollEnd() {
      if (typeof shouldSnap === 'function' && !shouldSnap()) return;
      snapStatListToNearest(statList);
    }

    if ('onscrollend' in window) {
      statList.addEventListener('scrollend', onScrollEnd, { passive: true });
      return;
    }

    statList.addEventListener('scroll', function () {
      window.clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(onScrollEnd, 120);
    }, { passive: true });
  }

  function initProductResults(section) {
    if (!section || section.dataset.productResultsInited === 'true') return;
    section.dataset.productResultsInited = 'true';

    const tabs = section.querySelectorAll('[data-product-results-tab]');
    const statsPanels = section.querySelectorAll('[data-product-results-stats-panel]');
    const mediaPanels = section.querySelectorAll('[data-product-results-media-panel]');
    const statsColumn = section.querySelector('.product-results__stats-column');

    if (!tabs.length || !statsPanels.length) return;

    function isMobileViewport() {
      return window.matchMedia('(max-width: 1023px)').matches;
    }

    function getHeaderOffset() {
      const headerHeightVar = window
        .getComputedStyle(document.documentElement)
        .getPropertyValue('--header-height')
        .trim();
      const parsed = Number.parseFloat(headerHeightVar);
      if (!Number.isFinite(parsed)) return 0;
      return parsed;
    }

    function scrollToStatsColumn() {
      if (!statsColumn || !isMobileViewport()) return;

      const sectionRect = section.getBoundingClientRect();
      const statsRect = statsColumn.getBoundingClientRect();
      const relativeTop = statsRect.top - sectionRect.top;
      const headerOffset = getHeaderOffset();
      const visualGap = 12;
      const targetY = window.scrollY + sectionRect.top + relativeTop - headerOffset - visualGap;
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      window.scrollTo({
        top: Math.max(0, targetY),
        behavior: prefersReducedMotion ? 'auto' : 'smooth'
      });
    }

    for (const statList of section.querySelectorAll('[data-product-results-stat-list]')) {
      initStatListScroll(statList, isMobileViewport);
    }

    function showPanel(blockId) {
      for (const tab of tabs) {
        const isActive = tab.getAttribute('data-product-results-block-id') === blockId;
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
        tab.classList.toggle('product-results__card--active', isActive);
      }

      for (const panel of statsPanels) {
        const isActive = panel.getAttribute('data-product-results-block-id') === blockId;
        panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');

        if (isActive) {
          panel.removeAttribute('inert');
          const statList = panel.querySelector('[data-product-results-stat-list]');
          if (statList) statList.scrollTop = 0;
        } else {
          panel.setAttribute('inert', '');
        }
      }

      for (const panel of mediaPanels) {
        const isActive = panel.getAttribute('data-product-results-block-id') === blockId;
        const wasHidden = panel.hasAttribute('inert');

        if (isActive) {
          panel.removeAttribute('inert');
          if (wasHidden) playPanelVideos(panel);
        } else {
          panel.setAttribute('inert', '');
          pausePanelVideos(panel);
        }
      }
    }

    function handleTabClick(event) {
      if (event.target.closest('[data-product-results-shop]')) return;

      const blockId = event.currentTarget.getAttribute('data-product-results-block-id');
      if (!blockId) return;

      showPanel(blockId);
      scrollToStatsColumn();
    }

    function handleTabKeydown(event) {
      if (event.target.closest('[data-product-results-shop]')) return;

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        const blockId = event.currentTarget.getAttribute('data-product-results-block-id');
        if (blockId) showPanel(blockId);
      }
    }

    function handleTablistKeydown(event) {
      const delta = VERTICAL_ARROW_KEYS[event.key];
      const tabArray = Array.from(tabs);
      const currentIdx = tabArray.findIndex(function (tab) {
        return tab.getAttribute('aria-selected') === 'true';
      });

      if (event.key === 'Home') {
        event.preventDefault();
        if (currentIdx <= 0) return;
        const blockId = tabArray[0].getAttribute('data-product-results-block-id');
        if (!blockId) return;
        showPanel(blockId);
        tabArray[0].focus();
        return;
      }

      if (event.key === 'End') {
        event.preventDefault();
        if (currentIdx === -1 || currentIdx >= tabArray.length - 1) return;
        const blockId = tabArray[tabArray.length - 1].getAttribute('data-product-results-block-id');
        if (!blockId) return;
        showPanel(blockId);
        tabArray[tabArray.length - 1].focus();
        return;
      }

      if (delta === undefined) return;

      event.preventDefault();

      if (currentIdx === -1) return;

      const nextIdx = currentIdx + delta;
      if (nextIdx < 0 || nextIdx >= tabArray.length) return;

      const blockId = tabArray[nextIdx].getAttribute('data-product-results-block-id');
      if (!blockId) return;

      showPanel(blockId);
      tabArray[nextIdx].focus();
    }

    for (const tab of tabs) {
      tab.addEventListener('click', handleTabClick);
      tab.addEventListener('keydown', handleTabKeydown);

      const shopLink = tab.querySelector('[data-product-results-shop]');
      if (shopLink) {
        shopLink.addEventListener('click', function (event) {
          event.stopPropagation();
        });
      }
    }

    const tablist = section.querySelector('[role="tablist"]');
    if (tablist) {
      tablist.addEventListener('keydown', handleTablistKeydown);
    }

    const activeTab = section.querySelector('[data-product-results-tab][aria-selected="true"]');
    if (activeTab) {
      const blockId = activeTab.getAttribute('data-product-results-block-id');
      if (blockId) showPanel(blockId);
    }
  }

  function initAll(container) {
    const root = container || document;
    root.querySelectorAll('[data-product-results]').forEach(initProductResults);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    const section = event.target.querySelector('[data-product-results]');
    if (!section) return;
    delete section.dataset.productResultsInited;
  });
})();
